// Worcester Polytechnic Institute: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - WPI's Common Data Set 2025-2026 (WPI_CDS_2025-2026.pdf, posted on
//    wpi.edu; the site refuses scripted downloads, so it was fetched through
//    a browser session). C1 applicants/admits/enrollees for Fall 2025 with
//    the residency table; C8 test policy for Fall 2027; C21/C22 early plan
//    counts (one early decision count and one early action count, each
//    covering both rounds).
//  - WPI Undergraduate Admissions: Apply to WPI (the "WPI Application
//    Deadlines & Decision Dates" table), Application Options (the binding
//    Early Decision agreement), How to Apply and Test-Optional Admissions.
//    The pages carry no year; they are the pages as posted for the
//    application that opens in fall 2026 (Application Options advertises a
//    September 28, 2026 Early Decision session). Quirk: the decision dates
//    differ between pages. The deadline table says mid December for Early
//    Action I, mid February for Early Action II and mid March for Regular
//    Decision; Application Options says late January, late February and
//    late March; the CDS gives 2/1 and 3/1 for the two early action rounds.
//    The table is used and the difference is stated in the note.
//  - WPI Financial Aid, 2026-2027 Cost of Attendance for First-Year
//    Undergraduate Students, living on campus (its total, $87,570, equals
//    the sum of its items). The CDS G1 lists the same tuition, housing and
//    food; it leaves transportation as "varies".
//  - College Scorecard, school id 168421 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'WPI Common Data Set 2025-2026',
        url: 'https://www.wpi.edu/sites/default/files/2026-05/WPI_CDS_2025-2026.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'WPI Undergraduate Admissions, Apply to WPI',
        url: 'https://www.wpi.edu/admissions/undergraduate/apply',
        read: '2026-10-01',
    },
    options: {
        label: 'WPI Undergraduate Admissions, Application Options',
        url: 'https://www.wpi.edu/admissions/undergraduate/apply/application-options',
        read: '2026-10-01',
    },
    howTo: {
        label: 'WPI Undergraduate Admissions, How to Apply',
        url: 'https://www.wpi.edu/admissions/undergraduate/apply/how-to',
        read: '2026-10-01',
    },
    tests: {
        label: 'WPI Undergraduate Admissions, Test-Optional Admissions',
        url: 'https://www.wpi.edu/admissions/undergraduate/apply/how-to/test-optional-admissions',
        read: '2026-10-01',
    },
    costs: {
        label: 'WPI Financial Aid, Cost of Attendance',
        url: 'https://www.wpi.edu/admissions/tuition-aid/cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?168421-Worcester-Polytechnic-Institute',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'worcester-polytechnic-institute',
    kbId: 'worcester_polytechnic_institute',
    name: 'Worcester Polytechnic Institute',
    shortName: 'WPI',
    location: 'Worcester, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (WPI Marketing Communications resources); decoration only.
    branding: {
        primary: '#AC2B37',
        secondary: '#A9B0B7',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/worcester_polytechnic_institute_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 12355,
        admitted: 7647,
        enrolled: 1300,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 3920, admitted: 2571 },
            { group: 'Out-of-state', applied: 6735, admitted: 4414 },
            { group: 'International', applied: 1700, admitted: 662 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'WPI\'s Common Data Set reports one first-year acceptance rate for the whole institute, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision and early action',
        note: 'The Common Data Set gives one early decision count and one early action count for fall 2025, each covering both rounds.',
        rows: [
            { plan: 'Early Decision I and II', applied: 397, admitted: 310 },
            { plan: 'Early Action I and II', applied: 8882, admitted: 6151 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Early December', binding: true },
            { plan: 'Early Action I', deadline: 'November 1', decision: 'Mid December', binding: false },
            { plan: 'Early Decision II', deadline: 'January 5', decision: 'Mid February', binding: true },
            { plan: 'Early Action II', deadline: 'January 5', decision: 'Mid February', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'Mid March', binding: false },
        ],
        note: 'Decision dates are from WPI\'s deadline table. Its Application Options page gives later ones: late January for Early Action I, late February for Early Action II and late March for Regular Decision. Admitted students enroll by January 15 (Early Decision I), March 15 (Early Decision II) or May 1 (all other plans). Every first-year applicant uses the Common App, and the application fee is $70. January entrance is also offered.',
        source: 'apply',
        binding: 'Early Decision is binding: if you are admitted, you withdraw your applications to other colleges, file no new ones, and enroll at WPI. You sign an Early Decision Agreement when you apply. Every student admitted Early Decision is offered a merit scholarship of at least $25,000 a year, for up to four years. Early Action is not binding.',
        bindingSource: 'options',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'WPI is test-optional: you choose in the application whether your SAT or ACT scores are considered, and WPI says the choice will not count against you. If you opt in, WPI uses your best superscored SAT or your best ACT composite. You may self-report scores; official scores are needed only if you enroll. Scores are not used for merit scholarships.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'howTo',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 63936 },
            { item: 'Undergraduate student life fee', amount: 492 },
            { item: 'Health and wellness fee', amount: 810 },
            { item: 'New student orientation fee', amount: 200 },
            { item: 'Housing (on campus)', amount: 10660 },
            { item: 'Food', amount: 9072 },
            { item: 'Books, course materials, supplies and equipment', amount: 1200 },
            { item: 'Personal expenses', amount: 1200 },
        ],
        total: 87570,
        note: 'For a first-year student living on campus. WPI lists no transportation cost for students on campus. Every student must have health insurance; without it, WPI bills a plan that costs $2,945 for 2026-2027, which is not in this total.',
        source: 'costs',
        netPrice: {
            average: 43885,
            byIncome: [
                { income: '$0 to $30,000', amount: 36899 },
                { income: '$30,001 to $48,000', amount: 31014 },
                { income: '$48,001 to $75,000', amount: 35703 },
                { income: '$75,001 to $110,000', amount: 35535 },
                { income: 'Over $110,000', amount: 48711 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from WPI\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
